import type { PostedWithin, Seniority } from "@/lib/api/catalogue";
import type { Currency, EnvoyWorkSetting, StandingOrder } from "@/lib/api/envoy";
import { visitorLanguages } from "@/lib/languages";

/**
 * The envoy form's draft and where a first one starts. Pure: every
 * function here returns a new value.
 */

/** The most characters the instructions take, as envoy holds them. */
export const MAX_INSTRUCTIONS = 255;

/** The form as it is being edited. The pay is the text in its box. */
export interface EnvoyDraft {
    model: string | null;
    titles: string[];
    skipTitles: string[];
    mustMention: string[];
    seniority: Seniority[];
    /** ISO 639-1. Empty means any language. */
    languages: string[];
    currency: Currency;
    pay: string;
    keepUnstatedPay: boolean;
    postedWithin: PostedWithin | null;
    workSettings: EnvoyWorkSetting[];
    countries: string[];
    order: StandingOrder;
    instructions: string;
}

/** A first envoy: remote, anywhere, any age, in English, finding and tailoring. */
export const EMPTY_DRAFT: EnvoyDraft = {
    model: null,
    titles: [],
    skipTitles: [],
    mustMention: [],
    seniority: [],
    languages: ["en"],
    currency: "USD",
    pay: "",
    keepUnstatedPay: true,
    postedWithin: null,
    workSettings: ["REMOTE"],
    countries: [],
    order: "TAILOR",
    instructions: "",
};

/**
 * A first envoy for this visitor: in the languages their browser asks
 * for, else English.
 */
export function firstDraft(asked: readonly string[] = visitorLanguages()): EnvoyDraft {
    return { ...EMPTY_DRAFT, languages: asked.length > 0 ? [...asked] : ["en"] };
}

/** Whether the work asks the person to turn up somewhere: hybrid or on-site. */
export function turnsUp(settings: readonly EnvoyWorkSetting[]): boolean {
    return settings.includes("HYBRID") || settings.includes("ON_SITE");
}
